// PHA-573: full AI Insight panel for the patient chart tab.
// Reuses the same building blocks as AiInsightDrawer (TrendSparkline,
// ActionBlock, CitationLink) but with more vertical space and a
// history list of the last 14 superseded rows for context.

import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Box, Button, Card, Flex, Heading, Text, TextArea } from '@radix-ui/themes';
import { ReloadIcon, ThickArrowDownIcon, ThickArrowUpIcon } from '@radix-ui/react-icons';
import { aiInsightsApi } from '../../../services/api';
import type { AiInsightDetailResponse, PatientAiInsight } from '../types';
import TrendSparkline from './TrendSparkline';
import ActionBlock from './ActionBlock';
import CitationLink from './CitationLink';
import AiInsightHistoryList from './AiInsightHistoryList';

interface AiInsightChartTabProps {
  patientId: string;
  organizationId?: string;
}

const tierColor = (tier: PatientAiInsight['risk_tier']): 'red' | 'orange' | 'green' => {
  if (tier === 'critical') return 'red';
  if (tier === 'watch') return 'orange';
  return 'green';
};

const formatComputedAt = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export const AiInsightChartTab: React.FC<AiInsightChartTabProps> = ({
  patientId,
  organizationId,
}) => {
  const [active, setActive] = useState<PatientAiInsight | null>(null);
  const [history, setHistory] = useState<PatientAiInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [feedbackPosting, setFeedbackPosting] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState<string | null>(null);
  const [showWrongReason, setShowWrongReason] = useState(false);
  const [wrongReason, setWrongReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: AiInsightDetailResponse = await aiInsightsApi.get(
        patientId,
        { includeHistory: true, organizationId },
      );
      setActive(data.insight ?? null);
      setHistory(data.history ?? []);
    } catch (err) {
      setError((err as Error).message || 'Failed to load AI insight');
    } finally {
      setLoading(false);
    }
  }, [patientId, organizationId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const { status, data } = await aiInsightsApi.refresh(patientId, organizationId);
      if (status === 429) {
        setError(`Rate limited — try again in ${data?.retry_after_seconds ?? 30}s`);
      } else if (status >= 400) {
        setError(data?.error || 'Refresh failed');
      } else if (data?.insight) {
        // Move the prior active to the top of history.
        if (active) setHistory((h) => [active, ...h].slice(0, 14));
        setActive(data.insight);
        setFeedbackSent(null);
        setShowWrongReason(false);
        setWrongReason('');
      }
    } catch (err) {
      setError((err as Error).message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, [patientId, organizationId, active]);

  const sendFeedback = useCallback(
    async (rating: 'useful' | 'not_useful' | 'wrong', reason?: string) => {
      if (!active) return;
      setFeedbackPosting(rating);
      try {
        await aiInsightsApi.feedback(active.insight_id, { rating, reason });
        setFeedbackSent(rating);
        setShowWrongReason(false);
      } catch (err) {
        setError((err as Error).message || 'Feedback failed');
      } finally {
        setFeedbackPosting(null);
      }
    },
    [active],
  );

  if (loading) {
    return (
      <Box mt="3">
        <Text size="2" color="gray">Loading AI insight…</Text>
      </Box>
    );
  }

  if (!active) {
    return (
      <Box mt="3">
        <Card variant="surface">
          <Flex direction="column" gap="2">
            <Text size="2">
              No AI insight has been computed for this patient yet, or this
              organization does not have SmartHub AI enabled.
            </Text>
            <Box>
              <Button variant="soft" onClick={handleRefresh} disabled={refreshing}>
                <ReloadIcon /> {refreshing ? 'Computing…' : 'Compute now'}
              </Button>
            </Box>
            {error && <Text size="1" color="red">{error}</Text>}
          </Flex>
        </Card>
      </Box>
    );
  }

  return (
    <Box mt="3">
      <Flex direction="column" gap="3">
        <Flex align="center" justify="between" gap="2" wrap="wrap">
          <Flex align="center" gap="2">
            <Badge color={tierColor(active.risk_tier)} variant="solid" size="2">
              {active.risk_tier.toUpperCase()}
            </Badge>
            <Heading size="3">SmartHub AI Insight</Heading>
            <Badge color="gray" variant="soft" size="1">Beta</Badge>
            {active.live_signal && (
              <Badge color="red" variant="soft" size="1">● LIVE</Badge>
            )}
          </Flex>
          <Button variant="soft" onClick={handleRefresh} disabled={refreshing}>
            <ReloadIcon /> {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Flex>

        {/* PHA-575 CDS framing — persistent banner */}
        <Card variant="surface" style={{ background: 'var(--amber-2)' }}>
          <Text size="1">
            <strong>Suggested — review before action.</strong> Not a diagnosis.
            Computed {formatComputedAt(active.computed_at)} · model{' '}
            {active.model_id || 'gpt-4.1'} · prompt {active.prompt_version}
          </Text>
        </Card>

        <Text size="3" weight="medium">{active.why_now}</Text>

        {active.trend_callouts.length > 0 && (
          <Card variant="classic">
            <Heading size="2" mb="2">Trends</Heading>
            <Flex direction="column" gap="2">
              {active.trend_callouts.map((t) => {
                const points = active.sparkline_series[t.sparkline_series_id] || [];
                const arrow =
                  t.direction === 'rising' ? <ThickArrowUpIcon /> :
                  t.direction === 'falling' ? <ThickArrowDownIcon /> : null;
                return (
                  <Flex key={t.sparkline_series_id} align="center" gap="3">
                    <Box style={{ width: 60, fontSize: 12, color: 'var(--gray-11)' }}>
                      {t.metric.toUpperCase()}
                    </Box>
                    <TrendSparkline points={points} width={140} height={28} ariaLabel={`${t.metric} ${t.direction}`} />
                    <Text size="2" style={{ flex: 1 }}>
                      {arrow}
                      {' '}{t.narrative}
                    </Text>
                  </Flex>
                );
              })}
            </Flex>
          </Card>
        )}

        <ActionBlock title="Recommended Action" action={active.ma_action} variant="ma" />
        {active.md_action && (
          <ActionBlock title="What the physician should consider" action={active.md_action} variant="md" />
        )}

        {active.citations.length > 0 && (
          <Box>
            <Text size="1" color="gray" weight="bold">Citations — click to open in chart</Text>
            <Flex gap="2" wrap="wrap" mt="2">
              {active.citations.map((c, idx) => (
                <CitationLink key={idx} citation={c} patientId={active.patient_id} />
              ))}
            </Flex>
          </Box>
        )}

        {error && <Text size="1" color="red">{error}</Text>}

        {feedbackSent ? (
          <Text size="1" color="gray">Feedback recorded — thank you.</Text>
        ) : (
          <Flex gap="2" align="center" wrap="wrap">
            <Button variant="soft" color="green" disabled={feedbackPosting !== null} onClick={() => sendFeedback('useful')}>
              👍 Useful
            </Button>
            <Button variant="soft" color="orange" disabled={feedbackPosting !== null} onClick={() => sendFeedback('not_useful')}>
              👎 Not useful
            </Button>
            <Button variant="soft" color="red" disabled={feedbackPosting !== null} onClick={() => setShowWrongReason((s) => !s)}>
              🚫 Wrong
            </Button>
          </Flex>
        )}
        {showWrongReason && (
          <Flex direction="column" gap="2">
            <TextArea
              placeholder="What did the insight get wrong? (required)"
              value={wrongReason}
              onChange={(e) => setWrongReason(e.target.value)}
            />
            <Button
              disabled={!wrongReason.trim() || feedbackPosting !== null}
              onClick={() => sendFeedback('wrong', wrongReason.trim())}
            >
              Submit
            </Button>
          </Flex>
        )}

        <AiInsightHistoryList history={history} />
      </Flex>
    </Box>
  );
};

export default AiInsightChartTab;
